import React from "react";

const AboutUs = () => {
  const values = [
    {
      title: "Integrity",
      description:
        "We uphold the highest standards of honesty and ethics in all our engagements. Also emphasis on data security.",
    },
    {
      title: "Excellence",
      description:
        "We are committed to deliver the highest quality service and results, exceeding client expectations.",
    },
    {
      title: "Innovation",
      description:
        "We focus on forward-thinking, creative solutions that drive business transformation.",
    },
    {
      title: "Transparency",
      description:
        "We believe in open communication and clear reporting so clients always understand their financial position.",
    },
    {
      title: "Trust",
      description:
        "We build long-term relationships grounded on reliability, prudence, and client-first decisions.",
    },
    {
      title: "Customer First",
      description:
        "Our clients' goals guide our recommendations, we prioritise their interests in every decision.",
    },
    {
      title: "Collaboration",
      description:
        "We believe in building strong partnerships with our clients to achieve sustainable success.",
    },
  ];

  const clients = [
    "Individual Investors",
    "High Net-Worth Individuals (HNIs)",
    "Corporate Houses, LLPs & Private Companies",
    "Educational Institutions, Trusts & Societies",
    "NGOs, Start-ups & MSMEs",
    "Government Departments, Public Sector Undertakings (PSUs)",
  ];

  return (
    <div className="bg-gray-100 text-gray-900">
      {/* Hero Section */}
      <section className="bg-primary text-white py-10 text-center flex flex-col items-center justify-center">
        <h1 className="text-4xl font-bold mb-4">About Us</h1>
        <p className="text-lg max-w-3xl mx-8 text-center font-serif">
          Empowering individuals and businesses through innovative financial and
          business solutions to foster growth, improve efficiency, and solve
          complex problems.
        </p>
      </section>

      {/* Mission & Vision side-by-side (responsive) */}
      <section className="py-8 px-6">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
          <div className="bg-white rounded-2xl p-6 shadow-md">
            <h2 className="text-2xl text-primary text-center font-extrabold mb-2">
              Our Mission
            </h2>
            <div className="h-1 w-24 mx-auto rounded bg-primary/20 mb-4" />
            <p className="text-base text-gray-700 text-justify leading-relaxed">
              To simplify investing through professional advice, innovative
              financial solutions, and unwavering commitment to our client's
              financial success.
            </p>
          </div>

          <div className="bg-primary/5 rounded-2xl p-6 border border-primary/10">
            <h2 className="text-2xl text-primary text-center font-extrabold mb-2">
              Our Vision
            </h2>
            <div className="h-1 w-20 mx-auto rounded bg-primary/30 mb-4" />
            <p className="text-base leading-relaxed italic text-gray-800 text-justify">
              To become one of India's most trusted and respected financial
              services firms by delivering sustainable wealth creation and
              exceptional client experiences.
            </p>
          </div>
        </div>
      </section>

      {/* Investment Philosophy (bigger with arrow SVGs) */}
      <section className="mb-8 px-6">
        <div className="max-w-5xl mx-auto bg-white rounded-2xl p-6 shadow-sm">
          <h2 className="text-3xl md:text-4xl font-extrabold mb-6 text-center">
            OUR INVESTMENT PHILOSOPHY
          </h2>

          <div className="flex items-center justify-center gap-4 mb-6 flex-wrap">
            <div className="px-5 py-2.5 rounded-full bg-primary text-white font-semibold text-base">
              Protect Capital
            </div>

            <div className="text-gray-400 flex items-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-6 h-6"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M9 18l6-6-6-6" />
              </svg>
            </div>

            <div className="px-5 py-2.5 rounded-full bg-primary text-white font-semibold text-base">
              Generate Consistent Returns
            </div>

            <div className="text-gray-400 flex items-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-6 h-6"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M9 18l6-6-6-6" />
              </svg>
            </div>

            <div className="px-5 py-2.5 rounded-full bg-primary text-white font-semibold text-base">
              Build Long-Term Wealth
            </div>
          </div>

          <p className="text-center text-gray-700 leading-relaxed text-sm md:text-base max-w-3xl mx-auto">
            We believe successful investing is built on discipline,
            diversification, risk management, and continuous portfolio
            monitoring rather than short-term market speculation.
          </p>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-8 px-8 bg-white">
        <h2 className="text-3xl text-black font-bold mb-12 text-center">
          Our Values
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-7xl mx-auto">
          {values.map((value, index) => (
            <div key={index} className="p-6 bg-gray-100 rounded-lg shadow">
              <h3 className="text-xl font-semibold mb-4 text-primary">
                {value.title}
              </h3>
              <p className="text-gray-700">{value.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-8 px-8">
        <h2 className="text-3xl font-bold mb-12 text-center">Why Choose Us?</h2>
        <div className="space-y-4 max-w-4xl mx-auto">
          {[
            "Client-Centric Approach",
            "Personalized & Tailored Approach",
            "Transparent & Ethical Practices",
            "Diversified Investment Strategies",
            "Technology-Enabled Investment Services",
            "Dedicated Relationship Management",
            "Long-Term Wealth Creation Focus",
          ].map((reason, index) => (
            <div
              key={index}
              className="flex items-start gap-4 p-4 bg-white rounded-lg shadow"
            >
              <span className="text-primary text-2xl font-bold">✔</span>
              <div className="text-gray-700 text-lg">{reason}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Our Clients */}
      <section className="py-8 px-8 bg-white">
        <h2 className="text-3xl text-primary font-bold mb-12 text-center">
          OUR CLIENTS
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {clients.map((client, index) => (
            <div
              key={index}
              className="group p-6 bg-gray-100 rounded-lg shadow hover:shadow-lg hover:bg-primary hover:scale-105 transform transition duration-300"
            >
              <p className="text-gray-700 text-lg font-semibold group-hover:text-white transition duration-300">
                {client}
              </p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default AboutUs;
